import Pubnub from 'pubnub';
import { FC, useCallback, useEffect, useRef } from 'react';
import { matchQuery, useQueryClient } from '@tanstack/react-query';

import { useAdminSelector } from 'admin/reducers';
import { videoKeys } from 'admin/queries/queryKeys';
import { selectActiveOrganizationId } from 'admin/reducers/user/selectors';
import { usePubnubFacade } from 'admin/hooks/pubnub/use-pubnub-facade';
import { usePubnubChannel } from 'admin/hooks/pubnub/use-pubnub-channel';
import type { GetVideoResult, GetVideosResult, PaginatedData } from 'types/videos';
import produce from 'immer';
import { getVideoQueryFn } from 'src/routes/admin/queries/videos';

enum MessageType {
	transcodingUpdated = 'transcoding_updated',
	transcodingCreated = 'transcoding_created',
	transcodingDeleted = 'transcoding_deleted',
}

interface TranscodingUpdatedMessageEvent extends Pubnub.MessageEvent {
	message: {
		type: MessageType;
		data: {
			id: string;
			organizationId: string;
			traceId: string;
		};
		name: string;
	};
}

function isTranscodingProcessMessageEvent(msgEvent: Pubnub.MessageEvent): msgEvent is TranscodingUpdatedMessageEvent {
	const type = msgEvent?.message?.type;

	return type === MessageType.transcodingUpdated || type === MessageType.transcodingCreated;
}

function isDeleteVideoMessageEvent(msgEvent: Pubnub.MessageEvent): msgEvent is TranscodingUpdatedMessageEvent {
	return msgEvent?.message?.type === MessageType.transcodingDeleted;
}

const VideoGalleryObserver: FC = () => {
	const client = useQueryClient();
	const videoIdRef = useRef<string>('');
	const pubnub = usePubnubFacade();
	const organizationId = useAdminSelector(selectActiveOrganizationId);

	useEffect(() => {
		const unsubscribe = client.getQueryCache().subscribe(async event => {
			if (!event) {
				return;
			}

			const videoKey = videoKeys.detail({ organizationId, videoId: videoIdRef.current });
			const isMatchKey = videoIdRef.current && matchQuery({ queryKey: videoKey, exact: true }, event.query);
			const paginateListQueryKey = videoKeys.list.paginate({ organizationId });

			if (event.type === 'updated' && isMatchKey && event.action.type === 'success') {
				const result = event.query.state.data as GetVideoResult;
				const video = result.transcoding;

				// update item in the pagination data array
				client.setQueriesData<PaginatedData<GetVideosResult>>({ queryKey: paginateListQueryKey }, pData => {
					if (!pData) {
						// fixme: prepend added video to the first page instead of invalidating the query
						// details: on dev/prod, but no at localhost, pData is undefined when video is added
						// as a temporary fix, invalidate the query to get the latest data
						client.invalidateQueries({ queryKey: paginateListQueryKey });
						return undefined;
					}

					const newPages = produce(pData.pages, draft => {
						let videoIndex = -1;
						let pageIndex = -1;

						draft.forEach((page, index) => {
							const targetIndex = page.transcodings.findIndex(t => t.id === video.id);

							if (targetIndex >= 0) {
								videoIndex = targetIndex;
								pageIndex = index;
							}
						});

						if (videoIndex >= 0) {
							draft[pageIndex].transcodings[videoIndex] = video;
						} else {
							draft[0].transcodings.unshift(video);
						}
					});

					return {
						...pData,
						pages: newPages,
					};
				});
			}
		});

		return () => unsubscribe();
	}, [client, organizationId]);

	const onMessage = useCallback(
		async (messageEvent: Pubnub.MessageEvent) => {
			const videoId = messageEvent?.message?.data?.id;

			if (isTranscodingProcessMessageEvent(messageEvent)) {
				const qKey = videoKeys.detail({ organizationId, videoId });
				videoIdRef.current = videoId;

				const qData = client.getQueryData(qKey);

				if (qData) {
					client.invalidateQueries({
						queryKey: qKey,
						refetchType: 'all',
					});
				} else {
					await client.fetchQuery({
						queryFn: () => getVideoQueryFn({ organizationId, videoId }),
						queryKey: videoKeys.detail({ organizationId, videoId }),
					});
				}
			}

			if (isDeleteVideoMessageEvent(messageEvent)) {
				const videoKey = videoKeys.detail({ organizationId, videoId });
				const paginateListQueryKey = videoKeys.list.paginate({ organizationId });

				// Remove video data from cache
				client.removeQueries({ queryKey: videoKey });

				// Update list of video, by removing the current one
				client.setQueriesData<PaginatedData<GetVideosResult>>({ queryKey: paginateListQueryKey }, pData => {
					if (!pData) {
						// fixme: update the pagination data array instead of invalidating the query
						client.invalidateQueries({ queryKey: paginateListQueryKey });
						return undefined;
					}

					const newPages = produce(pData.pages, draft => {
						draft.forEach(draftPage => {
							draftPage.transcodings = draftPage.transcodings.filter(t => t.id !== videoId);
						});
					});

					return { ...pData, pages: newPages };
				});
			}
		},
		[client, organizationId]
	);

	usePubnubChannel({
		pubnub,
		channel: organizationId ? `${organizationId}.videos` : undefined,
		enabled: Boolean(organizationId),
		onMessage,
	});

	return null;
};

export default VideoGalleryObserver;
